import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { db } from "../firebase/config";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { useAuth } from "../contexts/AuthContext";
import { format, subDays, addDays, isAfter, isSameDay } from "date-fns";
import { Edit, Trash2, ChevronDown } from "lucide-react";

const Container = styled.div`
  padding: 2rem;
  min-height: 100vh;
  background: #f9fafb;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 600;
`;

const GoalText = styled.p`
  font-size: 1rem;
  color: #555;
  margin-bottom: 1rem;
`;

const ProgressBarContainer = styled.div`
  background: #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
  height: 20px;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background: #38bdf8;
  width: ${({ width }) => width}%;
  transition: width 0.3s ease;
`;

const AddTaskContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;
`;

const Input = styled.input`
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #ddd;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.9rem;
`;

const Button = styled.button`
  background: #111;
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background: #333;
  }
`;

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const TaskItem = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-decoration: ${({ completed }) => (completed ? "line-through" : "none")};
  color: ${({ completed }) => (completed ? "#aaa" : "#111")};
  transition: 0.2s;
  cursor: pointer;

  &:hover {
    transform: scale(1.01);
  }
`;

const TaskText = styled.span`
  flex: 1;
`;

const Icons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const Dropdown = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: 110%;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0.5rem;
  z-index: 10;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
`;

const DayOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  padding: 0.25rem 0;
`;

const DateNavigator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
  gap: 1rem;
`;

const DAYS_FULL = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function ClientDetails() {
  const { id } = useParams();
  const { userId } = useAuth();
  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [editingTask, setEditingTask] = useState(null);
  const [loading, setLoading] = useState(false);

  // 🔹 Fetch client
  useEffect(() => {
    const fetchClient = async () => {
      try {
        if (!userId) return;
        const docRef = doc(db, "users", userId, "clients", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setClient({ id: docSnap.id, ...docSnap.data() });
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchClient();
  }, [id, userId]);

  // 🔹 Listen to client tasks
  useEffect(() => {
    if (!id) return;
    if (!userId) return;
    const tasksRef = collection(db, "users", userId, "clients", id, "tasks");
    const unsubscribe = onSnapshot(tasksRef, (snapshot) => {
      const fetched = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        completedDates: doc.data()?.completedDates || {},
      }));
      setTasks(fetched);
    });
    return () => unsubscribe();
  }, [id, userId]);

  // 🔹 Add or update task
  const addOrUpdateTask = async () => {
    if (!newTask.trim()) return;
    setLoading(true);
    try {
      if (!userId) return;
      if (editingTask) {
        await updateDoc(
          doc(db, "users", userId, "clients", id, "tasks", editingTask.id),
          {
            text: newTask,
            recurring: isRecurring,
            days: isRecurring ? selectedDays : [],
          }
        );
        setEditingTask(null);
      } else {
        await addDoc(collection(db, "users", userId, "clients", id, "tasks"), {
          text: newTask,
          completed: false,
          recurring: isRecurring,
          days: isRecurring ? selectedDays : [],
          createdAt: new Date(),
          completedDates: {},
          date: isRecurring ? null : selectedDate,
        });
      }
      setNewTask("");
      setIsRecurring(false);
      setSelectedDays([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Toggle complete (per day if recurring)
  const toggleComplete = async (task) => {
    if (!userId) return;
    const taskRef = doc(db, "users", userId, "clients", id, "tasks", task.id);
    if (task.recurring) {
      const todayKey = format(selectedDate, "yyyy-MM-dd");
      const done = task.completedDates?.[todayKey] ?? false;
      await updateDoc(taskRef, { [`completedDates.${todayKey}`]: !done });
    } else {
      await updateDoc(taskRef, { completed: !task.completed });
    }
  };

  // 🔹 Delete task
  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    if (!userId) return;
    await deleteDoc(doc(db, "users", userId, "clients", id, "tasks", taskId));
  };

  // 🔹 Start editing
  const editTask = (task) => {
    setEditingTask(task);
    setNewTask(task.text);
    setIsRecurring(task.recurring);
    setSelectedDays(task.days || []);
  };

  // 🔹 Toggle recurring days
  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };
  const selectEveryday = () => setSelectedDays(DAYS_FULL);

  // 🔹 Filter tasks for selected date
  const tasksForSelectedDate = tasks.filter((t) => {
    if (t.recurring) {
      if (!t.days || t.days.length === 0) return true;
      const todayName = format(selectedDate, "EEEE");
      return t.days.includes(todayName);
    }
    if (!t.date) return false;
    return isSameDay(t.date.toDate ? t.date.toDate() : t.date, selectedDate);
  });

  // 🔹 Progress
  const completedCount = tasksForSelectedDate.filter((t) =>
    t.recurring
      ? t.completedDates?.[format(selectedDate, "yyyy-MM-dd")] ?? false
      : t.completed
  ).length;
  const progress = tasksForSelectedDate.length
    ? Math.round((completedCount / tasksForSelectedDate.length) * 100)
    : 0;

  const goPrevDay = () => setSelectedDate(subDays(selectedDate, 1));
  const goNextDay = () => {
    if (!isAfter(addDays(selectedDate, 1), new Date()))
      setSelectedDate(addDays(selectedDate, 1));
  };

  if (!client) return <div>Loading client...</div>;

  return (
    <Container>
      <Header>
        <h1>{client.name}</h1>
      </Header>
      <p>Goal: {client.goal}</p>

      <DateNavigator>
        <Button onClick={goPrevDay}>← Previous</Button>
        <span>{format(selectedDate, "MMM dd, yyyy")}</span>
        <Button onClick={goNextDay}>Next →</Button>
      </DateNavigator>

      <ProgressBarContainer>
        <ProgressBarFill width={progress} />
      </ProgressBarContainer>
      <p>{progress}% Complete</p>

      <AddTaskContainer>
        <Input
          placeholder="Add or edit a task..."
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
        />
        <CheckboxLabel>
          <input
            type="checkbox"
            checked={isRecurring}
            onChange={(e) => {
              setIsRecurring(e.target.checked);
              if (!e.target.checked) setSelectedDays([]);
            }}
          />
          Recurring
          {isRecurring && (
            <Dropdown>
              <ChevronDown
                size={18}
                onClick={() => setDropdownOpen((prev) => !prev)}
                style={{ cursor: "pointer" }}
              />
              {dropdownOpen && (
                <DropdownMenu>
                  <DayOption>
                    <input
                      type="checkbox"
                      checked={selectedDays.length === 7}
                      onChange={selectEveryday}
                    />
                    Everyday
                  </DayOption>
                  {DAYS_FULL.map((day, i) => (
                    <DayOption key={day}>
                      <input
                        type="checkbox"
                        checked={selectedDays.includes(day)}
                        onChange={() => toggleDay(day)}
                      />
                      {DAYS_SHORT[i]}
                    </DayOption>
                  ))}
                </DropdownMenu>
              )}
            </Dropdown>
          )}
        </CheckboxLabel>
        <Button onClick={addOrUpdateTask}>
          {editingTask ? "Update" : "Add"} Task
        </Button>
      </AddTaskContainer>

      <TaskList>
        {tasksForSelectedDate.map((task) => {
          const completed = task.recurring
            ? task.completedDates?.[format(selectedDate, "yyyy-MM-dd")] ?? false
            : task.completed;
          return (
            <TaskItem
              key={task.id}
              completed={completed}
              onClick={() => toggleComplete(task)}
            >
              <span>{task.text}</span>
              <Icons onClick={(e) => e.stopPropagation()}>
                <Edit size={18} onClick={() => editTask(task)} />
                <Trash2 size={18} onClick={() => deleteTask(task.id)} />
              </Icons>
            </TaskItem>
          );
        })}
      </TaskList>
    </Container>
  );
}
